import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import styles from './ContactForm.module.scss';
import ButtonPrimary from '../ButtonPrimary';
import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import TextareaAutosize from 'react-textarea-autosize';
import { trackContactForm } from '../../utils/dataLayer';
import { Dialog, DialogDismiss, DialogHeading, useDialogState } from 'ariakit/dialog';

const numberOfEmployeesOptions = [
    { value: '0-10', label: '0-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-250', label: '51-250 employees' },
    { value: '> 250', label: '> 250 employees' },
];

const organisationTypeOptions = [
    { value: 'private', label: 'Private company' },
    { value: 'public', label: 'Public company' },
    { value: 'chamber', label: 'Chamber' },
    { value: 'confederation', label: 'Confederation' },
    { value: 'business', label: 'Business association' },
    { value: 'other', label: 'Other' },
];

const meetingTypeOptions = [
    { value: 'congress', label: 'Congress' },
    { value: 'conference', label: 'Conference' },
];

const ContactForm = ({ formProps, recaptchaToken, csrf }) => {
    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            help: formProps?.help?.defaultValue,
            employees: '',
            organisationType: '',
            meetingType: '',
            contact: 'email',
        },
    });

    const dialog = useDialogState({ animated: true });
    const [ref, isVisible] = useIntersectionObserver();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!dialog.open) reset();
    }, [dialog.open, reset]);

    const URL = '/wt/api/nextjs/v1/send_contact_form/';

    const getLabel = (options, value) => {
        const option = options.find((option) => option.value === value);
        return option ? option.label : '';
    };
    const handleSubmitForm = async (data) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            if (!executeRecaptcha) throw new Error('reCAPTCHA not ready');
            const token = await executeRecaptcha('contact_form');
            if (!token) throw new Error('Failed to get reCAPTCHA token');

            const organisationTypeLabel = getLabel(organisationTypeOptions, data.organisationType);
            const meetingTypeLabel = getLabel(meetingTypeOptions, data.meetingType);
            const employeesLabel = getLabel(numberOfEmployeesOptions, data.employees);

            const formData = new FormData();
            formData.append('recaptcha_token', token);
            formData.append('title', data.title);
            formData.append('first_name', data.firstName);
            formData.append('last_name', data.lastName);
            formData.append('email', data.email);
            formData.append('phonenumber', data.phonenumber);
            formData.append('help', data.help);
            formData.append('contact', data.contact);
            formData.append('message', data.message);
            formData.append('employees', employeesLabel);
            formData.append('company', data.company);
            formData.append('country', data.country);
            formData.append('industry', data.industry);
            formData.append('organisation_type', organisationTypeLabel);
            formData.append('website', data.website);
            formData.append('meeting_type', meetingTypeLabel);

            const response = await fetch(URL, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'X-CSRFToken': csrf,
                },
                body: formData,
                credentials: 'include',
            });

            if (!response.ok) {
                const parsed = await response.json().catch(() => null);
                console.error('Submit failed', parsed);
                throw new Error(parsed?.message || 'reCAPTCHA verification failed.');
            }

            trackContactForm(data.help);
            dialog.show();
        } catch (error) {
            console.error('Error:', error.message);
        } finally {
            setSubmitting(false);
        }
    };

    // const contact = watch('contact');
    const helpValue = watch('help');
    const { t } = useTranslation();

    const formClasses = classNames(styles['Root'], 'black-glass', {
        [styles['Visible']]: isVisible,
    });

    useEffect(() => {
        reset({
            help: helpValue,
            title: watch('title'),
            firstName: watch('firstName'),
            lastName: watch('lastName'),
            email: watch('email'),
            phonenumber: watch('phonenumber'),
            contact: watch('contact'),
            employees: '',
            organisationType: '',
            meetingType: '',
        });
    }, [helpValue, reset]);

    const renderConditionalFields = () => {
        switch (helpValue) {
            case 'invest-setup-expand-in-stockholm':
                return (
                    <>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('company'),
                                    [styles['HasError']]: errors.company,
                                })}>
                                <input
                                    id="company"
                                    className={styles['Input']}
                                    autoComplete="organization"
                                    aria-invalid={!!errors.company}
                                    aria-describedby={errors.company ? 'company-error' : null}
                                    maxLength={70}
                                    {...register('company', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="company">
                                    {t('ContactForm.labels.company')} *
                                </label>
                            </div>
                            {errors.company && <ErrorMessage id="company-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['SelectWrapper'], {
                                    [styles['HasValue']]: watch('employees'),
                                    [styles['HasError']]: errors.employees,
                                })}>
                                <select
                                    className={styles['Select']}
                                    {...register('employees', { required: true })}
                                    aria-invalid={!!errors.employees}
                                    aria-describedby={errors.employees ? 'employees-error' : null}
                                    id="employees">
                                    {numberOfEmployeesOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <label className={styles['Label']} htmlFor="employees">
                                    {t('ContactForm.labels.employeesOption')} *
                                </label>
                                <i className={styles['Icon']} />
                            </div>
                            {errors.employees && (
                                <ErrorMessage id="employees-error">This field is required</ErrorMessage>
                            )}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['SelectWrapper'], {
                                    [styles['HasValue']]: watch('organisationType'),
                                    [styles['HasError']]: errors.organisationType,
                                })}>
                                <select
                                    className={styles['Select']}
                                    {...register('organisationType', { required: true })}
                                    aria-invalid={!!errors.organisationType}
                                    aria-describedby={errors.organisationType ? 'organisationType-error' : null}
                                    id="organisationType">
                                    {organisationTypeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <label className={styles['Label']} htmlFor="organisationType">
                                    {t('ContactForm.labels.organisationTypeOption')} *
                                </label>
                                <i className={styles['Icon']} />
                            </div>
                            {errors.organisationType && (
                                <ErrorMessage id="organisationType-error">This field is required</ErrorMessage>
                            )}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('country'),
                                    [styles['HasError']]: errors.country,
                                })}>
                                <input
                                    id="country"
                                    className={styles['Input']}
                                    autoComplete="country-name"
                                    aria-invalid={!!errors.country}
                                    aria-describedby={errors.country ? 'country-error' : null}
                                    maxLength={70}
                                    {...register('country', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="country">
                                    {t('ContactForm.labels.country')} *
                                </label>
                            </div>
                            {errors.country && <ErrorMessage id="country-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('industry'),
                                    [styles['HasError']]: errors.industry,
                                })}>
                                <input
                                    id="industry"
                                    className={styles['Input']}
                                    autoComplete="off"
                                    aria-invalid={!!errors.industry}
                                    aria-describedby={errors.industry ? 'industry-error' : null}
                                    maxLength={70}
                                    {...register('industry', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="industry">
                                    {t('ContactForm.labels.industry')} *
                                </label>
                            </div>
                            {errors.industry && <ErrorMessage id="industry-error">This field is required</ErrorMessage>}
                        </div>
                        <div
                            className={classNames(styles['Field'], styles['TextareaField'], {
                                [styles['HasValue']]: watch('message'),
                            })}>
                            <TextareaAutosize className={styles['Textarea']} id="message" {...register('message')} />
                            <label className={styles['Label']} htmlFor="message">
                                {t('ContactForm.labels.message')}
                            </label>
                        </div>
                    </>
                );
            case 'recruit-or-relocate':
                return (
                    <>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('company'),
                                    [styles['HasError']]: errors.company,
                                })}>
                                <input
                                    id="company"
                                    className={styles['Input']}
                                    autoComplete="organization"
                                    aria-invalid={!!errors.company}
                                    aria-describedby={errors.company ? 'company-error' : null}
                                    maxLength={70}
                                    {...register('company', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="company">
                                    {t('ContactForm.labels.company')} *
                                </label>
                            </div>
                            {errors.company && <ErrorMessage id="company-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('country'),
                                    [styles['HasError']]: errors.country,
                                })}>
                                <input
                                    id="country"
                                    className={styles['Input']}
                                    autoComplete="country-name"
                                    maxLength={70}
                                    {...register('country', { required: false })}
                                />
                                <label className={styles['Label']} htmlFor="country">
                                    {t('ContactForm.labels.country')}
                                </label>
                            </div>
                        </div>
                    </>
                );
            case 'organize-congress-or-conference':
                return (
                    <>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('company'),
                                    [styles['HasError']]: errors.company,
                                })}>
                                <input
                                    id="company"
                                    className={styles['Input']}
                                    autoComplete="organization"
                                    aria-invalid={!!errors.company}
                                    aria-describedby={errors.company ? 'company-error' : null}
                                    maxLength={70}
                                    {...register('company', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="company">
                                    {t('ContactForm.labels.company')} *
                                </label>
                            </div>
                            {errors.company && <ErrorMessage id="company-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['SelectWrapper'], {
                                    [styles['HasValue']]: watch('meetingType'),
                                    [styles['HasError']]: errors.meetingType,
                                })}>
                                <select
                                    className={styles['Select']}
                                    {...register('meetingType', { required: true })}
                                    aria-invalid={!!errors.meetingType}
                                    aria-describedby={errors.meetingType ? 'meetingType-error' : null}
                                    id="meetingType">
                                    {meetingTypeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <label className={styles['Label']} htmlFor="meetingType">
                                    {t('ContactForm.labels.meetingTypeOption')} *
                                </label>
                                <i className={styles['Icon']} />
                            </div>
                            {errors.meetingType && (
                                <ErrorMessage id="meetingType-error">This field is required</ErrorMessage>
                            )}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('country'),
                                    [styles['HasError']]: errors.country,
                                })}>
                                <input
                                    id="country"
                                    className={styles['Input']}
                                    autoComplete="country-name"
                                    aria-invalid={!!errors.country}
                                    aria-describedby={errors.country ? 'country-error' : null}
                                    maxLength={70}
                                    {...register('country', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="country">
                                    {t('ContactForm.labels.country')} *
                                </label>
                            </div>
                            {errors.country && <ErrorMessage id="country-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('website'),
                                })}>
                                <input
                                    id="website"
                                    className={styles['Input']}
                                    autoComplete="url"
                                    maxLength={70}
                                    {...register('website', { required: false })}
                                />
                                <label className={styles['Label']} htmlFor="website">
                                    {t('ContactForm.labels.website')}
                                </label>
                            </div>
                        </div>
                        <div
                            className={classNames(styles['Field'], styles['TextareaField'], {
                                [styles['HasValue']]: watch('message'),
                            })}>
                            <TextareaAutosize className={styles['Textarea']} id="message" {...register('message')} />
                            <label className={styles['Label']} htmlFor="message">
                                {t('ContactForm.labels.message')}
                            </label>
                        </div>
                    </>
                );
            case 'organize-major-event':
                return (
                    <>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('company'),
                                    [styles['HasError']]: errors.company,
                                })}>
                                <input
                                    id="company"
                                    className={styles['Input']}
                                    autoComplete="organization"
                                    aria-invalid={!!errors.company}
                                    aria-describedby={errors.company ? 'company-error' : null}
                                    maxLength={70}
                                    {...register('company', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="company">
                                    {t('ContactForm.labels.company')} *
                                </label>
                            </div>
                            {errors.company && <ErrorMessage id="company-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('country'),
                                    [styles['HasError']]: errors.country,
                                })}>
                                <input
                                    id="country"
                                    className={styles['Input']}
                                    autoComplete="country-name"
                                    maxLength={70}
                                    {...register('country', { required: false })}
                                />
                                <label className={styles['Label']} htmlFor="country">
                                    {t('ContactForm.labels.country')}
                                </label>
                            </div>
                        </div>
                    </>
                );
            case 'promote-stockholm-as-destination':
                return (
                    <>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('company'),
                                    [styles['HasError']]: errors.company,
                                })}>
                                <input
                                    id="company"
                                    className={styles['Input']}
                                    autoComplete="organization"
                                    aria-invalid={!!errors.company}
                                    aria-describedby={errors.company ? 'company-error' : null}
                                    maxLength={70}
                                    {...register('company', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="company">
                                    {t('ContactForm.labels.company')} *
                                </label>
                            </div>
                            {errors.company && <ErrorMessage id="company-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('country'),
                                    [styles['HasError']]: errors.country,
                                })}>
                                <input
                                    id="country"
                                    className={styles['Input']}
                                    autoComplete="country-name"
                                    aria-invalid={!!errors.country}
                                    aria-describedby={errors.country ? 'country-error' : null}
                                    maxLength={70}
                                    {...register('country', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="country">
                                    {t('ContactForm.labels.country')} *
                                </label>
                            </div>
                            {errors.country && <ErrorMessage id="country-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('website'),
                                })}>
                                <input
                                    id="website"
                                    className={styles['Input']}
                                    autoComplete="url"
                                    maxLength={70}
                                    {...register('website', { required: false })}
                                />
                                <label className={styles['Label']} htmlFor="website">
                                    {t('ContactForm.labels.website')}
                                </label>
                            </div>
                        </div>
                        <div
                            className={classNames(styles['Field'], styles['TextareaField'], {
                                [styles['HasValue']]: watch('message'),
                            })}>
                            <TextareaAutosize className={styles['Textarea']} id="message" {...register('message')} />
                            <label className={styles['Label']} htmlFor="message">
                                {t('ContactForm.labels.message')}
                            </label>
                        </div>
                    </>
                );
            case 'cover-stockholm-internationally':
                //KLAR
                return null;
            case 'stockholm-insights':
                //KLAR
                return null;
            default:
                return null;
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit(handleSubmitForm)} noValidate ref={ref} className={formClasses}>
                <div className={styles['FieldsetWrapper']}>
                    <p className={styles['Description']}>{t('ContactForm.description')}</p>
                    <fieldset className={styles['Fieldset']}>
                        <legend className={styles['Legend']}>{t('ContactForm.labels.howCanWeHelp')}</legend>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['SelectWrapper'], {
                                    [styles['HasValue']]: watch('help'),
                                    [styles['HasError']]: errors.help,
                                })}>
                                <select
                                    className={styles['Select']}
                                    {...register('help', { required: true })}
                                    aria-invalid={!!errors.help}
                                    aria-describedby={errors.help ? 'help-error' : null}
                                    id="help">
                                    {formProps?.help?.values?.map((value) => (
                                        <option key={value[0]} value={value[0]}>
                                            {value[1]}
                                        </option>
                                    ))}
                                </select>
                                <label className={styles['Label']} htmlFor="help">
                                    {t('ContactForm.labels.selectOption')} *
                                </label>
                                <i className={styles['Icon']} />
                            </div>
                            {errors.help && <ErrorMessage id="help-error">This field is required</ErrorMessage>}
                        </div>
                    </fieldset>
                    <fieldset className={styles['Fieldset']}>
                        <legend className={styles['Legend']}>{t('ContactForm.legends.contactInfo')}</legend>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('title'),
                                    [styles['HasError']]: errors.title,
                                })}>
                                <input
                                    id="title"
                                    className={styles['Input']}
                                    autoComplete="organization-title"
                                    aria-invalid={!!errors.title}
                                    aria-describedby={errors.title ? 'title-error' : null}
                                    maxLength={70}
                                    {...register('title', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="title">
                                    {t('ContactForm.labels.title')} *
                                </label>
                            </div>
                            {errors.lastName && <ErrorMessage id="title-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('firstName'),
                                    [styles['HasError']]: errors.firstName,
                                })}>
                                <input
                                    id="firstName"
                                    className={styles['Input']}
                                    autoComplete="given-name"
                                    aria-invalid={!!errors.firstName}
                                    aria-describedby={errors.firstName ? 'firstName-error' : null}
                                    maxLength={70}
                                    {...register('firstName', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="firstName">
                                    {t('ContactForm.labels.firstName')} *
                                </label>
                            </div>
                            {errors.firstName && (
                                <ErrorMessage id="firstName-error">This field is required</ErrorMessage>
                            )}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('lastName'),
                                    [styles['HasError']]: errors.lastName,
                                })}>
                                <input
                                    id="lastName"
                                    className={styles['Input']}
                                    autoComplete="family-name"
                                    aria-invalid={!!errors.lastName}
                                    aria-describedby={errors.lastName ? 'lastName-error' : null}
                                    maxLength={70}
                                    {...register('lastName', { required: true })}
                                />
                                <label className={styles['Label']} htmlFor="lastName">
                                    {t('ContactForm.labels.lastName')} *
                                </label>
                            </div>
                            {errors.lastName && <ErrorMessage id="lastName-error">This field is required</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('email'),
                                    [styles['HasError']]: errors.email,
                                })}>
                                <input
                                    id="email"
                                    className={styles['Input']}
                                    autoComplete="email"
                                    maxLength={150}
                                    aria-invalid={!!errors.email}
                                    aria-describedby={errors.email ? 'email-error' : null}
                                    {...register('email', {
                                        required: t('ContactForm.errors.required'),
                                        pattern: {
                                            value: /\S+@\S+\.\S+/,
                                            message: t('ContactForm.errors.validEmail'),
                                        },
                                    })}
                                />
                                <label className={styles['Label']} htmlFor="email">
                                    {t('ContactForm.labels.email')} *
                                </label>
                            </div>
                            {errors.email && <ErrorMessage id="email-error">{errors.email.message}</ErrorMessage>}
                        </div>
                        <div className={styles['Field']}>
                            <div
                                className={classNames(styles['InputField'], {
                                    [styles['HasValue']]: watch('phonenumber'),
                                    [styles['HasError']]: errors.phonenumber,
                                })}>
                                <input
                                    className={styles['Input']}
                                    id="phonenumber"
                                    maxLength={30}
                                    autoComplete="tel"
                                    aria-labelledby="phoneNum phoneHelp"
                                    aria-describedby="phonenumber-error"
                                    {...register('phonenumber', {
                                        required: true,
                                    })}
                                />
                                <label id="phoneNum" className={styles['Label']} htmlFor="phonenumber">
                                    {t('ContactForm.labels.phone')} *
                                </label>
                            </div>
                            <div id="phoneHelp" className={styles['HelpText']}>
                                {t('ContactForm.help.phone')}
                            </div>
                            {errors.phonenumber && (
                                <ErrorMessage id="phonenumber-error">{t('ContactForm.errors.required')}</ErrorMessage>
                            )}
                        </div>
                        {renderConditionalFields()}
                    </fieldset>
                    {/* <fieldset className={styles['Fieldset']}>
            <legend className={`${styles['Legend']} ${styles['NoMarginBottom']}`}>
              {t('ContactForm.legends.preferedWay')}
            </legend>
            <p className={styles['HelpTextContact']}>
              {t('ContactForm.labels.likeToContact')}
            </p>
            <div className={styles['RadioGroupWrapper']}>
              <div className={styles['RadioWrapper']}>
                <input
                  {...register('contact')}
                  type="radio"
                  value="email"
                  className={styles['Radio']}
                  id="contact-by-email"
                />
                <label className={styles['RadioLabel']} htmlFor="contact-by-email">
                  {contact === 'email' && (
                    <Image
                      width={12}
                      height={20}
                      src={`/img/icon-check.svg`}
                      alt=""
                      className={styles['Check']}
                    />
                  )}
                  {t('ContactForm.labels.email')}
                </label>
              </div>
              <div className={styles['RadioWrapper']}>
                <input
                  {...register('contact')}
                  type="radio"
                  value="phone"
                  className={styles['Radio']}
                  id="contact-by-phone"
                />
                <label className={styles['RadioLabel']} htmlFor="contact-by-phone">
                  {contact === 'phone' && (
                    <Image
                      width={12}
                      height={20}
                      src={`/img/icon-check.svg`}
                      alt=""
                      className={styles['Check']}
                    />
                  )}
                  {t('ContactForm.labels.phoneShort')}
                </label>
              </div>
              <div className={styles['RadioWrapper']}>
                <input
                  {...register('contact')}
                  type="radio"
                  value="no-preference"
                  className={styles['Radio']}
                  id="contact-no-preference"
                />
                <label className={styles['RadioLabel']} htmlFor="contact-no-preference">
                  {contact === 'no-preference' && (
                    <Image
                      width={12}
                      height={20}
                      src={`/img/icon-check.svg`}
                      alt=""
                      className={styles['Check']}
                    />
                  )}
                  {t('ContactForm.labels.noPreference')}
                </label>
              </div>
            </div>
          </fieldset> */}
                </div>

                <div className={styles['PrivacyWrapper']}>
                    <input
                        type="checkbox"
                        className={styles['Checkbox']}
                        name="acceptPrivacy"
                        id="acceptPrivacy"
                        aria-invalid={!!errors.acceptPrivacy}
                        aria-describedby={errors.acceptPrivacy ? 'privacy-error' : null}
                        {...register('acceptPrivacy', { required: true })}
                    />
                    <label
                        dangerouslySetInnerHTML={{
                            __html: formProps?.privacyPolicy?.label,
                        }}
                        className={styles['CheckboxLabel']}
                        htmlFor="acceptPrivacy"
                    />
                </div>
                {errors.acceptPrivacy && <ErrorMessage id="privacy-error">This field is required</ErrorMessage>}

                {!!errors && Object.keys(errors).length > 0 && (
                    <div className={styles['ErrorListWrapper']} role="alert">
                        <ul className={styles['ErrorList']}>
                            {Object.keys(errors).map((key) => {
                                return (
                                    <li key={key} className={styles['ErrorListItem']}>
                                        <Image
                                            width={16}
                                            height={16}
                                            src={`/img/icon-warning.svg`}
                                            alt=""
                                            className={styles['Warning']}
                                        />
                                        <Link href={`#${key}`} className={styles['LinkError']}>
                                            {t(`ContactForm.errors.${key}`)}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
                <div className={styles['ButtonWrapper']}>
                    <ButtonPrimary
                        type="submit"
                        icon="icon-send"
                        disabled={submitting}
                        modifiers={['InlineFlex', 'CenterText', 'NarrowIconGap', 'BigButton']}>
                        {t('ContactForm.labels.send')}
                    </ButtonPrimary>
                </div>
                <small className={styles['ReCaptchaDisclaimer']}>
                    {t('ContactForm.recaptcha.protectedBy')}
                    <a href="https://policies.google.com/privacy">{t('ContactForm.recaptcha.privacyPolicy')}</a>
                    {t('ContactForm.recaptcha.and')}
                    <a href="https://policies.google.com/terms">{t('ContactForm.recaptcha.termsOfService')}</a>
                    {t('ContactForm.recaptcha.apply')}
                </small>
            </form>
            <Dialog
                backdropProps={{ className: styles['Backdrop'] }}
                state={dialog}
                className={`${styles['Dialog']} black-glass`}>
                <DialogHeading className={styles['DialogHeader']}>{t('ContactForm.dialog.headline')}</DialogHeading>
                <div className={`${styles['DialogContainer']}`}>{t('ContactForm.dialog.body')}</div>
                <div className={`${styles['ButtonContainer']}`}>
                    <ButtonPrimary modifiers={['TextAlignCenter']} onClick={dialog.close} asLink={true} href="/">
                        {t('ContactForm.dialog.button')}
                    </ButtonPrimary>
                </div>
                <DialogDismiss className={styles['Dismiss']}>
                    <span className="sr-only">{t('ContactForm.dialog.button')}</span>
                </DialogDismiss>
            </Dialog>
        </>
    );
};

const ErrorMessage = ({ children, id }) => {
    return (
        <div id={id} className={styles['Error']}>
            <Image width={16} height={16} src={`/img/icon-warning.svg`} alt="" className={styles['Warning']} />
            {children}
        </div>
    );
};

ContactForm.propTypes = {};

export default ContactForm;
